import { mockup } from '@growdash/design-system/assets';
import {
  Button,
  Card,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography
} from '@growdash/design-system/atoms';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

const companySizes = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '501+', label: '501+ employees' }
];

type CompanyInfo = {
  name: string;
  size: string;
};

interface CompanyInfoStepProps {
  companyInfo: CompanyInfo;
  onSubmit: (info: CompanyInfo) => void;
}

const CompanyInfoSchema = Yup.object().shape({
  name: Yup.string().required('Company name is required'),
  size: Yup.string().required('Company size is required')
});

export default function CompanyInfoStep({
  companyInfo: initialCompanyInfo,
  onSubmit
}: CompanyInfoStepProps) {
  const form = useForm<CompanyInfo>({
    resolver: yupResolver(CompanyInfoSchema),
    defaultValues: initialCompanyInfo
  });

  const handleSubmit = (data: CompanyInfo) => {
    onSubmit(data);
  };

  return (
    <div className='bg-white'>
      <div className='container relative hidden h-lvh bg-white flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0'>
        <div className='absolute inset-0 bg-gradient-to-r from-[#BADAFF33] via-transparent to-[#02D2C533]' />
        <div className='lg:p-8 z-10'>
          <div className='mx-auto flex w-full flex-col justify-center space-y-6'>
            <div className='flex flex-col space-y-2 text-center'>
              <div>
                <Typography variant='h3' className='text-[#191420] text-left'>
                  How do you plan to use GrowIntel?
                </Typography>
                <Typography
                  variant='h6'
                  className='mb-6 text-[#191420] text-left font-light'
                >
                  We&apos;ll use this to recommend the best path to get you
                  started.
                </Typography>
              </div>
              <Card className='p-6 bg-white border-none'>
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(handleSubmit)}
                    className='space-y-6 z-10'
                  >
                    <FormField
                      control={form.control}
                      name='name'
                      render={({ field }) => (
                        <FormItem className='text-left'>
                          <FormLabel className='text-[#191420] text-left'>
                            Company Name
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder='Enter your company name'
                              className='text-[#191420]'
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='size'
                      render={({ field }) => (
                        <FormItem className='text-left text-[#191420]'>
                          <FormLabel className='text-[#191420] text-left'>
                            Company Size
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue
                                  placeholder='Select company size'
                                  className='text-[#191420]/80'
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className='text-[#191420] bg-white'>
                              {companySizes.map(size => (
                                <SelectItem
                                  key={size.value}
                                  value={size.value}
                                  className='text-black'
                                >
                                  {size.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type='submit'
                      className='w-full bg-[#060081] hover:bg-[#060081]/80 text-white'
                    >
                      Continue
                    </Button>
                  </form>
                </Form>
              </Card>
            </div>
          </div>
        </div>
        <div className='relative hidden h-full flex-col p-10 text-foreground lg:flex'>
          <img
            src={mockup}
            alt='step1'
            className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
          />
        </div>
      </div>
    </div>
  );
}
